import { useEffect, useRef, useState } from "react";
import { useDebounce } from "hooks/useDebounce";
import { fetchCategorySearchApi } from "hooks/category-manage";

interface Category {
  id: number;
  name: string;
}

interface Props {
  value?: Category | null;
  onSelect: (category: Category) => void;
  placeholder?: string;
}

export default function SearchableCategoryDropdown({
  value,
  onSelect,
  placeholder = "Search category...",
}: Props) {
  const [query, setQuery] = useState(value?.name || "");
  const [options, setOptions] = useState<Category[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const isUserTypingRef = useRef(false);

  const debouncedQuery = useDebounce(query, 400);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Update query when value prop changes (for initialData/prefill)
  useEffect(() => {
    if (value?.name && !isUserTypingRef.current) {
      setQuery(value.name);
    } else if (!value && !isUserTypingRef.current) {
      setQuery("");
    }
  }, [value?.name, value?.id]);

  /* 🔍 Fetch categories */
  useEffect(() => {
    if (!debouncedQuery.trim()) {
      setOptions([]);
      return;
    }

    const load = async () => {
      try {
        setLoading(true);
        const res = await fetchCategorySearchApi(debouncedQuery);
        setOptions(res);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [debouncedQuery]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (!dropdownRef.current?.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleSelect = (item: Category) => {
    isUserTypingRef.current = false;
    onSelect(item);
    setQuery(item.name);
    setOpen(false);
  };

  return (
    <div ref={dropdownRef} className="relative w-full">
      <input
        value={query}
        placeholder={placeholder}
        onChange={(e) => {
          isUserTypingRef.current = true;
          setQuery(e.target.value);
          setOpen(true);
        }}
        onFocus={() => {
          setOpen(true);
          // If value exists, allow user to search by clearing query
          if (value?.name && query === value.name) {
            isUserTypingRef.current = false;
          }
        }}
        onBlur={() => {
          // Reset typing flag after a delay to allow value updates
          setTimeout(() => {
            isUserTypingRef.current = false;
          }, 200);
        }}
        className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:bg-slate-700 dark:border-slate-600 dark:text-white dark:focus:ring-blue-500"
      />

      {open && (
        <div className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-md border border-gray-200 bg-white shadow-lg">
          {loading && (
            <div className="px-3 py-2 text-sm text-gray-500">
              Searching...
            </div>
          )}

          {!loading && options.length === 0 && (
            <div className="px-3 py-2 text-sm text-gray-500">
              No results found
            </div>
          )}

          {options.map((item) => (
            <div
              key={item.id}
              onClick={() => handleSelect(item)}
              className="cursor-pointer px-3 py-2 text-sm hover:bg-blue-50"
            >
              {item.name}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
